import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableHead, TableRow,
  TableCell, TableBody, IconButton, Tooltip, Button, Alert, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Divider, Switch, FormControlLabel, Grid
} from '@mui/material';
import {
  Add, Edit, Delete, ArrowBack, Stairs, TrendingUp, Close
} from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';
import { fmtN as fmt, sym } from '../utils/currency';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const emptyGrade = { gradeCode: '', description: '', minSalary: '', maxSalary: '', midpoint: '' };
const emptyStep  = { stepNo: '', label: '', salary: '' };

const PayGradeList = () => {
  const navigate = useNavigate();

  // ── Grades ──
  const [grades,    setGrades]    = useState([]);
  const [gError,    setGError]    = useState('');
  const [gSuccess,  setGSuccess]  = useState('');
  const [gOpen,     setGOpen]     = useState(false);
  const [editGrade, setEditGrade] = useState(null);
  const [gForm,     setGForm]     = useState(emptyGrade);
  const { attemptClose, discardDialog } = useUnsavedGuard({ open: gOpen, value: gForm, onClose: () => setGOpen(false) });
  const [gSaving,   setGSaving]   = useState(false);
  const [gFormErr,  setGFormErr]  = useState('');

  // ── Delete confirmation dialogs ──
  const [gradeDlg, setGradeDlg] = useState(null);
  const [stepDlg,  setStepDlg]  = useState(null);

  // ── Steps dialog ──
  const [stepsGrade,  setStepsGrade]  = useState(null);  // grade object whose steps we're viewing
  const [steps,       setSteps]       = useState([]);
  const [stepsOpen,   setStepsOpen]   = useState(false);
  const [sForm,       setSForm]       = useState(emptyStep);
  const [editStep,    setEditStep]    = useState(null);
  const [sSaving,     setSSaving]     = useState(false);
  const [sError,      setSError]      = useState('');

  const loadGrades = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/employees/pay-grades/all`, { headers: hdrs() });
      setGrades(data);
    } catch { setGError('Failed to load pay grades'); }
  }, []);

  useEffect(() => { loadGrades(); }, [loadGrades]);

  const loadSteps = async (gradeCode) => {
    try {
      const { data } = await axios.get(`${API}/api/employees/pay-grades/${gradeCode}/steps`, { headers: hdrs() });
      setSteps(data);
    } catch { setSError('Failed to load steps'); }
  };

  // ── Grade CRUD ──
  const openAddGrade  = () => { setEditGrade(null); setGForm(emptyGrade); setGFormErr(''); setGOpen(true); };
  const openEditGrade = (g) => {
    setEditGrade(g);
    setGForm({
      gradeCode:   g.gradeCode,
      description: g.description,
      minSalary:   g.minSalary ?? '',
      maxSalary:   g.maxSalary ?? '',
      midpoint:    g.midpoint  ?? '',
      isActive:    g.isActive !== false,
    });
    setGFormErr(''); setGOpen(true);
  };
  const handleGSave = async () => {
    if (!gForm.gradeCode?.trim()) { setGFormErr('Grade code is required'); return; }
    if (!gForm.description?.trim()) { setGFormErr('Description is required'); return; }
    if (!gForm.minSalary) { setGFormErr('Min salary is required'); return; }
    if (!gForm.maxSalary) { setGFormErr('Max salary is required'); return; }
    setGSaving(true); setGFormErr('');
    try {
      if (editGrade) {
        await axios.put(`${API}/api/employees/pay-grades/${editGrade.gradeCode}`, gForm, { headers: hdrs() });
      } else {
        await axios.post(`${API}/api/employees/pay-grades`, gForm, { headers: hdrs() });
      }
      setGOpen(false);
      setGSuccess(editGrade ? 'Updated' : 'Created');
      setTimeout(() => setGSuccess(''), 3000);
      loadGrades();
    } catch (err) { setGFormErr(err.response?.data?.error || 'Save failed'); }
    finally { setGSaving(false); }
  };
  const confirmDeleteGrade = async () => {
    if (!gradeDlg) return;
    try {
      await axios.delete(`${API}/api/employees/pay-grades/${gradeDlg.gradeCode}`, { headers: hdrs() });
      setGSuccess('Deleted'); setGradeDlg(null); loadGrades();
      setTimeout(() => setGSuccess(''), 3000);
    } catch (err) { setGError(err.response?.data?.error || 'Delete failed'); }
  };

  // ── Steps CRUD ──
  const openSteps = async (grade) => {
    setStepsGrade(grade); setSError(''); setSForm(emptyStep); setEditStep(null);
    await loadSteps(grade.gradeCode);
    setStepsOpen(true);
  };
  const openEditStep = (s) => {
    setEditStep(s);
    setSForm({ stepNo: s.stepNo, label: s.label || '', salary: s.salary });
  };
  const handleSSave = async () => {
    if (!sForm.stepNo) { setSError('Step number is required'); return; }
    if (!sForm.salary) { setSError('Salary is required'); return; }
    const sal = Number(sForm.salary);
    const min = stepsGrade?.minSalary;
    const max = stepsGrade?.maxSalary;
    if (min != null && sal < min) {
      setSError(`Salary must be at least ${sym()} ${fmt(min)} (grade minimum)`); return;
    }
    if (max != null && sal > max) {
      setSError(`Salary must not exceed ${sym()} ${fmt(max)} (grade maximum)`); return;
    }
    setSSaving(true); setSError('');
    try {
      if (editStep) {
        await axios.put(
          `${API}/api/employees/pay-grades/${stepsGrade.gradeCode}/steps/${editStep.stepNo}`,
          sForm, { headers: hdrs() }
        );
      } else {
        await axios.post(
          `${API}/api/employees/pay-grades/${stepsGrade.gradeCode}/steps`,
          { ...sForm, label: sForm.label || `Step ${sForm.stepNo}` }, { headers: hdrs() }
        );
      }
      setSForm(emptyStep); setEditStep(null);
      await loadSteps(stepsGrade.gradeCode);
      loadGrades();
    } catch (err) { setSError(err.response?.data?.error || 'Save failed'); }
    finally { setSSaving(false); }
  };
  const confirmDeleteStep = async () => {
    if (!stepDlg) return;
    try {
      await axios.delete(
        `${API}/api/employees/pay-grades/${stepsGrade.gradeCode}/steps/${stepDlg.stepNo}`,
        { headers: hdrs() }
      );
      setStepDlg(null);
      await loadSteps(stepsGrade.gradeCode);
    } catch (err) { setSError(err.response?.data?.error || 'Delete failed'); }
  };

  // auto-suggest next step number
  const nextStep = steps.length > 0 ? Math.max(...steps.map(s => s.stepNo)) + 1 : 1;

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 4, mb: 4 }}>
      <Card elevation={3}>
        <CardContent>
          {/* Header */}
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Button startIcon={<ArrowBack />} size="small" onClick={() => navigate('/hr')}>Back</Button>
              <TrendingUp sx={{ color: '#546e7a' }} />
              <Typography variant="h5" fontWeight={700} color="#37474f">Pay Grades</Typography>
              <Chip label={`${grades.filter(g => g.isActive !== false).length} active`} size="small" color="success" />
            </Box>
            <Button variant="contained" startIcon={<Add />} onClick={openAddGrade}
              sx={{ bgcolor: '#546e7a', '&:hover': { bgcolor: '#37474f' } }}>
              Add Grade
            </Button>
          </Box>

          {gSuccess && <Alert severity="success" sx={{ mb: 2 }}>{gSuccess}</Alert>}
          {gError   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setGError('')}>{gError}</Alert>}

          <Divider sx={{ mb: 2 }} />

          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell><strong>Grade</strong></TableCell>
                <TableCell><strong>Description</strong></TableCell>
                <TableCell align="right"><strong>Min Salary</strong></TableCell>
                <TableCell align="right"><strong>Midpoint</strong></TableCell>
                <TableCell align="right"><strong>Max Salary</strong></TableCell>
                <TableCell align="center"><strong>Steps</strong></TableCell>
                <TableCell align="center"><strong>Status</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {grades.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary" py={3}>
                      No pay grades. Defaults are seeded automatically on first use.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : grades.map(g => (
                <TableRow key={g.gradeCode} hover sx={{ '&:nth-of-type(odd)': { bgcolor: 'grey.50' } }}>
                  <TableCell>
                    <Chip label={g.gradeCode} size="small" color="primary" variant="outlined"
                      sx={{ fontFamily: 'monospace', fontWeight: 700 }} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}
                      color={g.isActive === false ? 'text.disabled' : 'inherit'}>
                      {g.description}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="text.secondary">{sym()} {fmt(g.minSalary)}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="text.secondary">
                      {g.midpoint ? `${sym()} ${fmt(g.midpoint)}` : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="text.secondary">{sym()} {fmt(g.maxSalary)}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Manage Steps">
                      <Button size="small" startIcon={<Stairs fontSize="small" />}
                        onClick={() => openSteps(g)} sx={{ minWidth: 0, px: 1 }}>
                        Steps
                      </Button>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={g.isActive !== false ? 'Active' : 'Inactive'} size="small"
                      color={g.isActive !== false ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit"><IconButton size="small" color="primary" onClick={() => openEditGrade(g)}><Edit fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setGradeDlg(g)}><Delete fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* ── Grade dialog ── */}
      {discardDialog}
      <Dialog open={gOpen} onClose={attemptClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editGrade ? `Edit — ${editGrade.gradeCode}` : 'Add Pay Grade'}</DialogTitle>
        <DialogContent dividers>
          {gFormErr && <Alert severity="error" sx={{ mb: 2 }}>{gFormErr}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField label="Grade Code *" name="gradeCode" value={gForm.gradeCode || ''}
                onChange={e => setGForm(f => ({ ...f, gradeCode: e.target.value.toUpperCase() }))}
                size="small" fullWidth disabled={!!editGrade}
                helperText={editGrade ? 'Cannot change' : 'e.g. C1, G2, M1'}
                inputProps={{ style: { textTransform: 'uppercase' } }} />
            </Grid>
            <Grid item xs={8}>
              <TextField label="Description *" name="description" value={gForm.description || ''}
                onChange={e => setGForm(f => ({ ...f, description: e.target.value }))}
                size="small" fullWidth placeholder="e.g. Teacher (P1/P2)" />
            </Grid>
            <Grid item xs={4}>
              <TextField label={`Min Salary (${sym()}) *`} type="number" value={gForm.minSalary ?? ''}
                onChange={e => setGForm(f => ({ ...f, minSalary: e.target.value }))}
                size="small" fullWidth inputProps={{ min: 0 }} />
            </Grid>
            <Grid item xs={4}>
              <TextField label={`Midpoint (${sym()})`} type="number" value={gForm.midpoint ?? ''}
                onChange={e => setGForm(f => ({ ...f, midpoint: e.target.value }))}
                size="small" fullWidth inputProps={{ min: 0 }} helperText="Optional" />
            </Grid>
            <Grid item xs={4}>
              <TextField label={`Max Salary (${sym()}) *`} type="number" value={gForm.maxSalary ?? ''}
                onChange={e => setGForm(f => ({ ...f, maxSalary: e.target.value }))}
                size="small" fullWidth inputProps={{ min: 0 }} />
            </Grid>
            {editGrade && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={gForm.isActive !== false}
                    onChange={e => setGForm(f => ({ ...f, isActive: e.target.checked }))} />}
                  label="Active" />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={handleGSave} disabled={gSaving}
            sx={{ bgcolor: '#546e7a', '&:hover': { bgcolor: '#37474f' } }}>
            {gSaving ? 'Saving…' : editGrade ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Pay Grade Confirmation Dialog */}
      <Dialog open={Boolean(gradeDlg)} onClose={() => setGradeDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Pay Grade</DialogTitle>
        <DialogContent>
          <Typography>
            Delete grade <strong>{gradeDlg?.gradeCode} — {gradeDlg?.description}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGradeDlg(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDeleteGrade}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Pay Step Confirmation Dialog */}
      <Dialog open={Boolean(stepDlg)} onClose={() => setStepDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Pay Step</DialogTitle>
        <DialogContent>
          <Typography>
            Delete Step {stepDlg?.stepNo}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStepDlg(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDeleteStep}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* ── Steps dialog ── */}
      <Dialog open={stepsOpen} onClose={() => setStepsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <Stairs fontSize="small" />
              <span>Pay Steps — {stepsGrade?.gradeCode} ({stepsGrade?.description})</span>
            </Box>
            <IconButton size="small" onClick={() => setStepsOpen(false)}><Close fontSize="small" /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {sError && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setSError('')}>{sError}</Alert>}

          {/* Range reminder */}
          {stepsGrade && (
            <Box sx={{ p: 1.5, mb: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Salary range: {sym()} {fmt(stepsGrade.minSalary)} — {sym()} {fmt(stepsGrade.maxSalary)}
                {stepsGrade.midpoint ? `  ·  Midpoint: ${sym()} ${fmt(stepsGrade.midpoint)}` : ''}
              </Typography>
            </Box>
          )}

          {/* Existing steps */}
          {steps.length > 0 && (
            <Table size="small" sx={{ mb: 2 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell><strong>Step</strong></TableCell>
                  <TableCell><strong>Label</strong></TableCell>
                  <TableCell align="right"><strong>Salary ({sym()})</strong></TableCell>
                  <TableCell align="right"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {steps.map(s => (
                  <TableRow key={s.stepNo} hover
                    sx={{ bgcolor: editStep?.stepNo === s.stepNo ? 'primary.50' : 'inherit' }}>
                    <TableCell>
                      <Chip label={`Step ${s.stepNo}`} size="small" color="info" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{s.label || '—'}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>{fmt(s.salary)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" color="primary" onClick={() => openEditStep(s)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => setStepDlg(s)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {/* Add / edit step form */}
          <Divider sx={{ mb: 2 }} />
          <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase">
            {editStep ? `Editing Step ${editStep.stepNo}` : 'Add New Step'}
          </Typography>
          <Grid container spacing={1.5} mt={0.5} alignItems="flex-end">
            <Grid item xs={2}>
              <TextField label="Step #" type="number" value={sForm.stepNo}
                onChange={e => setSForm(f => ({ ...f, stepNo: e.target.value }))}
                size="small" fullWidth inputProps={{ min: 1 }}
                disabled={!!editStep}
                placeholder={String(nextStep)} />
            </Grid>
            <Grid item xs={4}>
              <TextField label="Label" value={sForm.label}
                onChange={e => setSForm(f => ({ ...f, label: e.target.value }))}
                size="small" fullWidth placeholder={`Step ${sForm.stepNo || nextStep}`} />
            </Grid>
            <Grid item xs={4}>
              {(() => {
                const sal = Number(sForm.salary);
                const min = stepsGrade?.minSalary;
                const max = stepsGrade?.maxSalary;
                const outOfRange = sForm.salary !== '' && (
                  (min != null && sal < min) || (max != null && sal > max)
                );
                return (
                  <TextField label="Salary *" type="number" value={sForm.salary}
                    onChange={e => setSForm(f => ({ ...f, salary: e.target.value }))}
                    size="small" fullWidth
                    error={outOfRange}
                    helperText={outOfRange
                      ? `Must be ${sym()} ${fmt(min)} – ${sym()} ${fmt(max)}`
                      : `${sym()} ${fmt(min)} – ${sym()} ${fmt(max)}`}
                    inputProps={{ min: min ?? 0, max: max ?? undefined, step: 1 }} />
                );
              })()}
            </Grid>
            <Grid item xs={2}>
              <Button variant="contained" size="small" onClick={handleSSave} disabled={sSaving} fullWidth
                sx={{ bgcolor: '#546e7a', '&:hover': { bgcolor: '#37474f' } }}>
                {sSaving ? '…' : editStep ? 'Update' : 'Add'}
              </Button>
              {editStep && (
                <Button size="small" fullWidth sx={{ mt: 0.5 }}
                  onClick={() => { setEditStep(null); setSForm(emptyStep); }}>
                  Cancel
                </Button>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStepsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PayGradeList;
